import { MIN_SPLIT_EDGE_MS, type TurnView } from "../../contracts/index";
import { clock } from "../format";

export interface Draft {
  readonly key: string;
  readonly sources: string[];
  readonly words: [number, number] | undefined;
  readonly start: number;
  readonly end: number;
  readonly speaker: string | null;
  readonly texts: string[];
  readonly wordTimes: [number, number][];
}

export interface DraftStack {
  past: Draft[][];
  now: Draft[];
  future: Draft[][];
}

export type Edge = "start" | "end";

const SHARED_MS = 60;

export const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
export const exact = (ms: number) => `${clock(ms)}.${String(Math.floor((ms % 1000) / 100))}`;

export function fromTurns(turns: readonly TurnView[]): Draft[] {
  return turns.map((t) => ({
    key: t.unit_id ?? String(t.idx),
    sources: [t.unit_id ?? ""],
    words: undefined,
    start: t.start_ms,
    end: t.end_ms,
    speaker: t.speaker_id,
    texts: t.words.map((w) => w.text),
    wordTimes: t.words.map((w) => [w.start_ms, w.end_ms]),
  }));
}

export function same(a: readonly Draft[], b: readonly Draft[]): boolean {
  return (
    a.length === b.length &&
    a.every((d, i) => {
      const o = b[i];
      return (
        o !== undefined &&
        d.key === o.key &&
        d.start === o.start &&
        d.end === o.end &&
        d.speaker === o.speaker &&
        JSON.stringify(d.words) === JSON.stringify(o.words) &&
        d.sources.join() === o.sources.join()
      );
    })
  );
}

export function describe(base: readonly Draft[], drafts: readonly Draft[]): string[] {
  const n = (key: string) => base.findIndex((b) => b.key === key || b.sources[0] === key) + 1;
  const out: string[] = [];
  for (const d of drafts) {
    const b = base.find((x) => x.key === d.key);
    if (d.sources.length > 1)
      out.push(`Turns ${d.sources.map((s) => String(n(s))).join(" and ")} merged · ${exact(d.start)}–${exact(d.end)}`);
    else if (d.words) {
      if (d.words[0] === 0)
        out.push(
          `Turn ${String(n(d.sources[0] ?? ""))} split at ${exact(drafts[drafts.indexOf(d) + 1]?.start ?? d.end)}`,
        );
    } else if (b && (b.start !== d.start || b.end !== d.end))
      out.push(`Turn ${String(n(d.key))}: ${exact(b.start)}–${exact(b.end)} → ${exact(d.start)}–${exact(d.end)}`);
  }
  return out;
}

export function undoStack(s: DraftStack): DraftStack {
  const prev = s.past.at(-1);
  return prev ? { past: s.past.slice(0, -1), now: prev, future: [s.now, ...s.future] } : s;
}

export function redoStack(s: DraftStack): DraftStack {
  const next = s.future[0];
  return next ? { past: [...s.past, s.now], now: next, future: s.future.slice(1) } : s;
}

export function draftErrors(drafts: readonly Draft[], total: number): string[] {
  const out: string[] = [];
  drafts.forEach((x, i) => {
    if (x.end <= x.start) out.push(`Turn ${String(i + 1)} ends before it starts`);
    if (x.start < 0 || x.end > total + 1) out.push(`Turn ${String(i + 1)} lies outside the recording`);
    if (x.words && x.end - x.start < MIN_SPLIT_EDGE_MS) out.push(`Turn ${String(i + 1)} is shorter than 0.5 s`);
  });
  return out;
}

export interface EdgeMove {
  index: number;
  edge: Edge;
  ms: number;
  alone: boolean;
}

export function withEdge(drafts: Draft[], { index, edge, ms, alone }: EdgeMove, total: number): Draft[] {
  const next = drafts.map((x) => ({ ...x }));
  const t = next[index];
  if (!t) return drafts;
  const old = t[edge];
  const value = Math.round(Math.max(0, Math.min(total, ms)));
  next[index] = { ...t, [edge]: value };
  if (!alone) {
    const ni = edge === "end" ? index + 1 : index - 1;
    const n = next[ni];
    const other = edge === "end" ? "start" : "end";
    if (n && Math.abs(n[other] - old) <= SHARED_MS) next[ni] = { ...n, [other]: value };
  }
  return next;
}

export function splitDraft(d: Draft, at: number): { error: string } | { first: Draft; second: Draft } {
  if (at - d.start < MIN_SPLIT_EDGE_MS || d.end - at < MIN_SPLIT_EDGE_MS)
    return { error: "Move the playhead at least 0.5 s inside the turn to split it" };
  const range = d.words ?? [0, d.texts.length];
  const local = d.wordTimes.findIndex((w) => w[0] >= at);
  const k = Math.min(Math.max(local < 0 ? d.texts.length - 1 : local, 1), d.texts.length - 1);
  if (d.texts.length < 2) return { error: "A turn with one word cannot be split" };
  const first: Draft = {
    ...d,
    key: `${d.key}:a`,
    words: [range[0], range[0] + k],
    end: at,
    texts: d.texts.slice(0, k),
    wordTimes: d.wordTimes.slice(0, k),
  };
  const second: Draft = {
    ...d,
    key: `${d.key}:b`,
    words: [range[0] + k, range[1]],
    start: at,
    texts: d.texts.slice(k),
    wordTimes: d.wordTimes.slice(k),
  };
  return { first, second };
}

type WordRange = [number, number];

function giveWord(d: Draft, n: Draft, dw: WordRange, nw: WordRange): [Draft, Draft] {
  return [
    {
      ...d,
      words: [dw[0], dw[1] - 1] as [number, number],
      texts: d.texts.slice(0, -1),
      wordTimes: d.wordTimes.slice(0, -1),
    },
    {
      ...n,
      words: [nw[0] - 1, nw[1]] as [number, number],
      texts: [d.texts.at(-1) ?? "", ...n.texts],
      wordTimes: [d.wordTimes.at(-1) ?? [d.end, d.end], ...n.wordTimes],
    },
  ];
}

function takeWord(d: Draft, n: Draft, dw: WordRange, nw: WordRange): [Draft, Draft] {
  return [
    {
      ...d,
      words: [dw[0], dw[1] + 1] as [number, number],
      texts: [...d.texts, n.texts[0] ?? ""],
      wordTimes: [...d.wordTimes, n.wordTimes[0] ?? [n.start, n.start]],
    },
    {
      ...n,
      words: [nw[0] + 1, nw[1]] as [number, number],
      texts: n.texts.slice(1),
      wordTimes: n.wordTimes.slice(1),
    },
  ];
}

export function moveWord(drafts: Draft[], d: Draft | undefined, direction: "give" | "take"): Draft[] | null {
  const i = drafts.indexOf(d as Draft);
  const n = drafts[i + 1];
  if (!d || !n || !d.words || !n.words || d.sources[0] !== n.sources[0]) return null;
  if (direction === "give" ? d.texts.length < 2 : n.texts.length < 2) return null;
  const [a, b] = direction === "give" ? giveWord(d, n, d.words, n.words) : takeWord(d, n, d.words, n.words);
  return drafts.map((x, j) => {
    if (j === i) return a;
    return j === i + 1 ? b : x;
  });
}

export function mergePair(
  a: Draft,
  b: Draft,
  speaker: string | null | undefined,
  base: readonly Draft[],
): Draft | null {
  let merged: Draft;
  if (a.words && b.words && a.sources[0] === b.sources[0])
    merged = {
      ...a,
      key: a.key,
      words: [a.words[0], b.words[1]],
      end: b.end,
      texts: [...a.texts, ...b.texts],
      wordTimes: [...a.wordTimes, ...b.wordTimes],
      speaker: speaker ?? a.speaker,
    };
  else if (!a.words && !b.words)
    merged = {
      ...a,
      key: `${a.key}+${b.key}`,
      sources: [...a.sources, ...b.sources],
      end: b.end,
      texts: [...a.texts, ...b.texts],
      wordTimes: [...a.wordTimes, ...b.wordTimes],
      speaker: speaker ?? a.speaker,
    };
  else return null;
  if (
    merged.words &&
    merged.words[0] === 0 &&
    merged.words[1] === (base.find((x) => x.key === merged.sources[0])?.texts.length ?? -1)
  )
    merged = { ...merged, words: undefined, key: merged.sources[0] ?? merged.key };
  return merged;
}

export function replacePair(drafts: readonly Draft[], a: Draft, b: Draft, merged: Draft): Draft[] {
  return drafts.flatMap((x) => {
    if (x === a) return [merged];
    return x === b ? [] : [x];
  });
}
